import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { useForm } from 'react-hook-form';
import { showToastNotification } from '../../utility/helper';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import { IGrade } from '../../interfaces/product';
import ConfirmationModal from '../../components/UI/Modal/ConfirmationModal';
import useConfirmationModal from '../../hooks/useConfirmationModal';

const Grade: React.FC = () => {
    const [grades, setGrades] = useState<IGrade[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({ mode: 'onBlur' });
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [editingGrade, setEditingGrade] = useState<IGrade | null>(null);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();

    const onSubmit = async (data: any) => {
        setLoadingButton(true);
        const postData = { title: data.title };

        try {
            if (editingGrade) {
                // Update grade
                await callApi(API_URL_2 + API_ENDPOINT.page + `grade/${editingGrade.id}`, { method: 'PUT', data: postData });
                showToastNotification('success', 'Grade updated successfully');
            } else {
                // Create new grade
                await callApi(API_URL_2 + API_ENDPOINT.page + 'grade', { method: 'POST', data: postData });
                showToastNotification('success', 'Grade added successfully');
            }

            reset();
            setEditingGrade(null);
            getGrades();
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    useEffect(() => {
        getGrades();
    }, []);

    const getGrades = async () => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + 'grade', { method: 'GET' });
            setGrades(response);
        } catch (error: any) {
            console.error('Failed to fetch grades:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteGrade = async (id: string) => {
        try {
            await callApi(API_URL_2 + API_ENDPOINT.page + `grade/${id}`, { method: 'DELETE' });
            setGrades((prevGrades: IGrade[]) => prevGrades.filter((grade) => grade.id !== id));
        } catch (error: any) {
            console.error('Failed to delete grade:', error.message);
        }
    };

    const handleEditGrade = (grade: IGrade) => {
        setEditingGrade(grade);
        setValue('title', grade.title);
    };

    const handleDeleteClick = (id: string) => {
        confirmAction({
            title: 'Confirm Delete',
            message: 'Are you sure you want to delete this grade?',
            onConfirm: () => deleteGrade(id),
        });
    };

    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">Grades</h1>
            </div>
            <div className="row">
                <div className="col-lg-8">
                    <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                        <div className="app-card-body">
                            <div className="table-responsive">
                                <table className="table app-table-hover mb-0 text-left">
                                    <thead>
                                        <tr>
                                            <th className="cell">S.no.</th>
                                            <th className="cell">Grade</th>
                                            <th className="cell">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td className="text-center" colSpan={3}>Loading grades...</td>
                                            </tr>
                                        ) : grades.length === 0 ? (
                                            <tr>
                                                <td className="text-center" colSpan={3}>No grades available to display.</td>
                                            </tr>
                                        ) : (
                                            grades.map((grade, index) => (
                                                <tr key={grade.id}>
                                                    <td className="cell">#{index + 1}</td>
                                                    <td className="cell">{grade.title}</td>
                                                    <td className="cell">
                                                        <div className=' d-flex align-items-center gap-1'>
                                                            <a className="btn btn-success px-2 py-1 text-white" href="javascript:void(0)" onClick={() => handleEditGrade(grade)}><i className="bi bi-pen"></i></a>
                                                            <a className="btn btn-danger px-2 py-1 text-white" href="javascript:void(0)" onClick={() => handleDeleteClick(grade.id)}><i className="bi bi-trash3"></i></a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="app-card app-card-settings shadow-sm p-4">
                        <div className="app-card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="row">
                                    <div className="mb-3 col-12">
                                        <InputField
                                            type="text"
                                            register={register('title', { required: 'Grade is required' })}
                                            name="title"
                                            label="Grade"
                                            placeholder="Enter grade"
                                        />
                                        <ErrorMessage message={errors.title?.message?.toString()} />
                                    </div>

                                </div>
                                <div className="col-12 text-end mt-3 ">
                                    <Button loading={loadingButton} className="btn btn-primary text-white px-5" type="submit" text={editingGrade ? 'Update' : 'Save'} />
                                </div>
                            </form>
                        </div>
                    </div>

                </div>
            </div>

            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    );
};

export default Grade;
